import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { SonicPreset, DEFAULT_PRESET, AudioSettings, DEFAULT_AUDIO_SETTINGS, GenerativeConstraints, ScaleType, RootNote, PlayMode } from '../types';
import { useAudioController } from '../hooks/useAudioController';
import { ComposerEngine } from '../services/composer/ComposerEngine';

interface AudioContextType {
    // State
    preset: SonicPreset;
    audioSettings: AudioSettings;
    isAudioStarted: boolean; // Restore this
    isLoading: boolean;
    isSceneChanging: boolean;
    audioEngine: React.MutableRefObject<any>;
    activeQuickStartId: string | null;

    // Actions
    handleStartAudio: (onComplete?: () => void) => Promise<void>;
    stopAudio: () => void;
    updatePreset: (preset: SonicPreset, isFromQuickStart?: boolean) => void;
    updateSettings: (settings: Partial<AudioSettings>, isFromQuickStart?: boolean) => void;
    setActiveQuickStartId: (id: string | null) => void;

    // Specific Handlers (Convenience)
    handleWaveformChange: (waveform: SonicPreset['waveform']) => void;
    handleResonanceChange: (resonance: number) => void;
    handleGlideToggle: () => void;

    // Constraint Management
    handleConstraintsChange: (constraints: Partial<GenerativeConstraints>, isFromQuickStart?: boolean) => void;
    handleRandomizeConstraints: () => void;

    // Composer Mode
    handleNewSong: () => void;

    // Reset
    handleResetAll: () => void;

    // Cinematic
    handleNextTimelessSong: () => void;
}

const AudioContext = createContext<AudioContextType | undefined>(undefined);

// Default settings for each instrument/waveform
const INSTRUMENT_DEFAULTS: Record<string, { resonance: number; reverb: number; delay: number; isGlideOn: boolean }> = {
    'piano': { resonance: 1, reverb: 30, delay: 10, isGlideOn: false },
    'electric-piano': { resonance: 3, reverb: 40, delay: 30, isGlideOn: false },
    'organ': { resonance: 2, reverb: 60, delay: 20, isGlideOn: true },
    'sine': { resonance: 3, reverb: 20, delay: 20, isGlideOn: true },
    'triangle': { resonance: 3, reverb: 20, delay: 20, isGlideOn: true },
    'sawtooth': { resonance: 3, reverb: 20, delay: 20, isGlideOn: true },
    'square': { resonance: 3, reverb: 20, delay: 20, isGlideOn: true },
};

export const AudioProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [preset, setPreset] = useState<SonicPreset>(DEFAULT_PRESET);
    const [audioSettings, setAudioSettings] = useState<AudioSettings>(DEFAULT_AUDIO_SETTINGS);
    const [activeQuickStartId, setActiveQuickStartId] = useState<string | null>(null);
    const [isFirstComposerLoad, setIsFirstComposerLoad] = useState(true);

    const {
        audioEngine,
        isAudioStarted,
        isLoading,
        isSceneChanging,
        handleStartAudio: startAudio,
        stopAudio
    } = useAudioController(preset, audioSettings);

    const updatePreset = useCallback((newPreset: SonicPreset, isFromQuickStart: boolean = false) => {
        setPreset(newPreset);
        if (!isFromQuickStart) {
            setActiveQuickStartId(null);
        }
    }, []);

    // Helper to apply composer settings
    const applyComposerSettings = useCallback((forcePiano: boolean = false) => {
        ComposerEngine.initialize(forcePiano);
        const suggestedInstrument = ComposerEngine.getSuggestedInstrument();
        const defaults = INSTRUMENT_DEFAULTS[suggestedInstrument] || INSTRUMENT_DEFAULTS['triangle'];

        // Auto-switch instrument
        setPreset(prev => ({
            ...prev,
            waveform: suggestedInstrument,
            resonance: defaults.resonance
        }));

        // Auto-switch audio settings (Reverb/Delay/Glide)
        setAudioSettings(prev => ({
            ...prev,
            reverb: defaults.reverb,
            delay: defaults.delay,
            isGlideOn: defaults.isGlideOn
        }));

        if (forcePiano) {
            setIsFirstComposerLoad(false);
        }
    }, []);

    const updateSettings = useCallback((newSettings: Partial<AudioSettings>, isFromQuickStart: boolean = false) => {
        setAudioSettings(prev => {
            const updated = { ...prev, ...newSettings };

            // AUTO-INIT COMPOSER: If switching INTO composer mode from something else
            if (newSettings.interactionMode === 'composer' && prev.interactionMode !== 'composer') {
                // Initialize asynchronously to avoid state update loops if called directly
                setTimeout(() => {
                    applyComposerSettings(isFirstComposerLoad);
                }, 0);
            }
            return updated;
        });

        if (!isFromQuickStart) {
            setActiveQuickStartId(null);
        }
    }, [applyComposerSettings, isFirstComposerLoad]);

    const handleStartAudio = useCallback(async (onComplete?: () => void) => {
        await startAudio(onComplete);
    }, [startAudio]);

    const handleWaveformChange = useCallback((waveform: SonicPreset['waveform']) => {
        const defaults = INSTRUMENT_DEFAULTS[waveform] || INSTRUMENT_DEFAULTS['triangle'];

        setPreset(prev => ({
            ...prev,
            waveform,
            resonance: defaults.resonance
        }));

        // Keep 'waveform' mode active since changing waveforms is the point of that mode
        if (activeQuickStartId !== 'waveform') {
            setActiveQuickStartId(null);
        }

        setAudioSettings(prev => ({
            ...prev,
            reverb: defaults.reverb,
            delay: defaults.delay,
            isGlideOn: defaults.isGlideOn
        }));
    }, [activeQuickStartId]);

    const handleResonanceChange = useCallback((resonance: number) => {
        setPreset(prev => ({ ...prev, resonance }));
        setActiveQuickStartId(null);
    }, []);

    const handleGlideToggle = useCallback(() => {
        setAudioSettings(prev => ({ ...prev, isGlideOn: !prev.isGlideOn }));
        setActiveQuickStartId(null);
    }, []);

    const handleConstraintsChange = useCallback((newConstraints: Partial<GenerativeConstraints>, isFromQuickStart: boolean = false) => {
        setAudioSettings(prev => ({
            ...prev,
            constraints: { ...prev.constraints, ...newConstraints }
        }));

        if (!isFromQuickStart) {
            setActiveQuickStartId(null);
        }
    }, []);

    const handleRandomizeConstraints = useCallback(() => {
        const scales: ScaleType[] = ['major', 'minor', 'pentatonic_major', 'pentatonic_minor', 'blues', 'dorian'];
        const roots: RootNote[] = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];

        const randomScale = scales[Math.floor(Math.random() * scales.length)];
        const randomRoot = roots[Math.floor(Math.random() * roots.length)];

        handleConstraintsChange({
            scale: randomScale,
            rootNote: randomRoot,
            enabled: true
        });
    }, [handleConstraintsChange]);

    /**
     * Re-initialize ComposerEngine for a new song with fresh random parameters
     */
    /**
     * Re-initialize ComposerEngine for a new song with fresh random parameters
     */
    const handleNewSong = useCallback(() => {
        applyComposerSettings(false); // Explicitly false for "New Song" button
    }, [applyComposerSettings]);

    const handleNextTimelessSong = useCallback(() => {
        const TIMELESS_MODES: PlayMode[] = [
            // Classical Masterpieces
            'bach_bwv_924',
            'chopin_op9_no1',
            'bach_fantasia_g_minor',
            'chopin_op15_no2',
            'chopin_prelude_op45',
            'chopin_op9_no2',
            'chopin_prelude_op28_no4',
            'chopin_fantaisie_impromptu',
            'gershwin_rhapsody_in_blue',
            'liszt_la_campanella',
            'debussy_clair_de_lune',
            'chopin_minute_waltz',
            'mozart_rondo_alla_turca',
            'mozart_sonata_facile',
            'mozart_fantasia_d_minor',
            'mozart_twinkle_variations',
            'mozart_elvira_madigan',
            'beethoven_fur_elise',
            'beethoven_moonlight_sonata',
            'satie_gymnopedie_no1',
            // Cinematic Epics
            'cinematic_interstellar',
            'cinematic_jaws',
            'cinematic_star_wars',
            'cinematic_james_bond',
            'cinematic_good_bad_ugly',
            'cinematic_mission_impossible',
            'cinematic_psycho',
            'cinematic_indiana_jones',
            'cinematic_pink_panther',
            'cinematic_harry_potter',
            'cinematic_halloween'
        ];

        const currentModeIndex = TIMELESS_MODES.indexOf(audioSettings.playMode);
        let nextIndex = 0;

        if (currentModeIndex !== -1) {
            nextIndex = (currentModeIndex + 1) % TIMELESS_MODES.length;
        }

        const nextMode = TIMELESS_MODES[nextIndex];
        setAudioSettings(prev => ({ ...prev, playMode: nextMode }));
    }, [audioSettings.playMode]);

    const handleResetAll = useCallback(() => {
        setPreset(DEFAULT_PRESET);
        setAudioSettings(DEFAULT_AUDIO_SETTINGS);
        setActiveQuickStartId(null);
    }, []);

    const value = React.useMemo(() => ({
        preset,
        audioSettings,
        isAudioStarted,
        isLoading,
        isSceneChanging,
        audioEngine,
        activeQuickStartId,
        handleStartAudio,
        stopAudio,
        updatePreset,
        updateSettings,
        setActiveQuickStartId,
        handleWaveformChange,
        handleResonanceChange,
        handleGlideToggle,
        handleConstraintsChange,
        handleRandomizeConstraints,
        handleNewSong,
        handleResetAll,
        handleNextTimelessSong
    }), [
        preset,
        audioSettings,
        isAudioStarted,
        isLoading,
        isSceneChanging,
        audioEngine,
        activeQuickStartId,
        handleStartAudio,
        stopAudio,
        updatePreset,
        updateSettings,
        setActiveQuickStartId,
        handleWaveformChange,
        handleResonanceChange,
        handleGlideToggle,
        handleConstraintsChange,
        handleRandomizeConstraints,
        handleNewSong,
        handleResetAll,
        handleNextTimelessSong
    ]);

    return (
        <AudioContext.Provider value={value}>
            {children}
        </AudioContext.Provider>
    );
};

export const useAudio = () => {
    const context = useContext(AudioContext);
    if (context === undefined) {
        throw new Error('useAudio must be used within an AudioProvider');
    }
    return context;
};
